import './styles/index.css';

import { parseCSV } from './services/csv-parser.service';
import { parseJSON } from './services/json-parser.service';
import { exportCSV } from './services/csv-export.service';
import { initGrid } from './services/grid.service';
import { setOriginalData, getGridApi } from './state/app-state';
import { resetFilterState, addCondition, addGroup, removeNode, updateNode, toggleMatchMode } from './state/filter-state';
import { renderFilterTree, setFilterCallbacks } from './filter/filter-renderer';
import { setupUploadZone, updateUploadZoneWithFileInfo } from './ui/upload-zone';
import { toggleSidebar, setupSidebar } from './ui/sidebar';
import { showError, hideError } from './ui/error-display';
import { showFieldSelector } from './ui/field-selector';
import { updateCounts, showStatusBar } from './ui/status-bar';
import { renderPresetManager, setupPresetManager, setPresetLoadCallback } from './ui/preset-manager';
import { getElement, toggleVisibility } from './utils/dom-utils';
import { checkServerAvailability, needsMigration, migrateLocalPresetsToServer } from './services/preset.service';
import type { CsvRow } from './types';

// Apply filters to grid
function applyFilters(): void {
  const gridApi = getGridApi();
  if (!gridApi) return;

  gridApi.setFilterModel(null);
  gridApi.onFilterChanged();
  updateCounts();
}

// Clear all filters
function clearAllFilters(): void {
  resetFilterState();
  renderFilterTree();
  applyFilters();
}

// Handle filter node updates
function handleUpdateNode(id: number | 'root', field: string, value: string): void {
  const needsRerender = updateNode(id, field as 'column' | 'operator' | 'value' | 'value2' | 'matchMode', value);
  if (needsRerender) {
    renderFilterTree();
  }
  applyFilters();
}

// Handle add condition
function handleAddCondition(parentId: number | 'root'): void {
  addCondition(parentId);
  renderFilterTree();
  applyFilters();
}

// Handle add group
function handleAddGroup(parentId: number | 'root'): void {
  addGroup(parentId);
  renderFilterTree();
  applyFilters();
}

// Handle remove node
function handleRemoveNode(id: number): void {
  removeNode(id);
  renderFilterTree();
  applyFilters();
}

// Handle toggle match mode
function handleToggleMatchMode(id: number | 'root'): void {
  toggleMatchMode(id);
  renderFilterTree();
  applyFilters();
}

// Setup global filter actions for inline handlers
window.filterActions = {
  updateNode: handleUpdateNode,
  removeNode: handleRemoveNode,
  addCondition: handleAddCondition,
  addGroup: handleAddGroup,
  toggleMatchMode: handleToggleMatchMode
};

// Show migration modal
function showMigrationModal(): void {
  // Remove any existing modal
  const existingModal = document.querySelector('.migration-modal-overlay');
  if (existingModal) {
    existingModal.remove();
  }

  const overlay = document.createElement('div');
  overlay.className = 'migration-modal-overlay';
  overlay.innerHTML = `
    <div class="migration-modal">
      <h3>Lokale Filter zum Server hochladen?</h3>
      <p>Sie haben Filter-Presets im lokalen Browser-Speicher. Möchten Sie diese jetzt auf den Server hochladen, damit alle Benutzer darauf zugreifen können?</p>
      <div class="migration-modal-actions">
        <button id="migrationNoBtn" class="migration-btn migration-btn-secondary">Nein</button>
        <button id="migrationYesBtn" class="migration-btn migration-btn-primary">Ja</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const yesBtn = overlay.querySelector('#migrationYesBtn') as HTMLButtonElement;
  const noBtn = overlay.querySelector('#migrationNoBtn') as HTMLButtonElement;

  yesBtn?.addEventListener('click', async () => {
    yesBtn.disabled = true;
    yesBtn.textContent = 'Wird hochgeladen...';

    const result = await migrateLocalPresetsToServer();

    if (result.success && result.migrated > 0) {
      alert(`${result.migrated} Preset(s) erfolgreich hochgeladen!`);
    } else if (result.errors.length > 0) {
      alert(`Einige Presets konnten nicht hochgeladen werden:\n${result.errors.join('\n')}`);
    }

    overlay.remove();
    // Refresh page to reload presets from server
    location.reload();
  });

  noBtn?.addEventListener('click', () => {
    // Don't set migrated flag - show prompt again next session
    overlay.remove();
  });
}

// Check for migration on app start
async function checkMigration(): Promise<void> {
  const serverAvailable = await checkServerAvailability();

  if (serverAvailable && needsMigration()) {
    showMigrationModal();
  }
}

// Handle JSON file: parse, flatten, show field selector, filter
async function handleJsonFile(file: File): Promise<CsvRow[]> {
  const loading = getElement('loading');
  const jsonResult = await parseJSON(file);

  if (jsonResult.data.length === 0) {
    throw new Error('JSON-Datei enthält keine Daten.');
  }

  // Hide loading while showing field selector
  toggleVisibility(loading, false);

  const selectedFields = await showFieldSelector(jsonResult.allKeys);

  // Show loading again while building grid
  toggleVisibility(loading, true);

  return jsonResult.data.map(row => {
    const filtered: CsvRow = {};
    for (const field of selectedFields) {
      filtered[field] = row[field] ?? '';
    }
    return filtered;
  });
}

// Handle file processing
async function handleFile(file: File): Promise<void> {
  hideError();

  const loading = getElement('loading');
  const gridContainer = getElement('gridContainer');
  const exportBtn = getElement<HTMLButtonElement>('exportBtn');

  toggleVisibility(loading, true);
  toggleVisibility(gridContainer, false);

  try {
    let data: CsvRow[];

    if (file.name.endsWith('.json')) {
      data = await handleJsonFile(file);
    } else {
      const result = await parseCSV(file);
      if (!result.data || result.data.length === 0) {
        showError('CSV file is empty.');
        toggleVisibility(loading, false);
        return;
      }
      data = result.data;
    }

    setOriginalData(data);

    // Initialize grid
    if (gridContainer) {
      initGrid(
        gridContainer,
        data,
        async () => {
          updateCounts();
          // Inject preset manager into sidebar (only if not already present)
          const sidebarContent = document.querySelector('.sidebar-content');
          const existingPresetManager = document.querySelector('.preset-manager');
          if (sidebarContent && !existingPresetManager) {
            const presetHtml = await renderPresetManager();
            const filterConditions = document.getElementById('filterConditions');
            if (filterConditions) {
              filterConditions.insertAdjacentHTML('beforebegin', presetHtml);
              await setupPresetManager();
            }
          }
        },
        updateCounts
      );
    }

    // Update UI
    toggleVisibility(loading, false);
    toggleVisibility(gridContainer, true);
    showStatusBar();

    if (exportBtn) {
      exportBtn.disabled = false;
    }

    updateUploadZoneWithFileInfo(file.name, data.length, handleFile);

  } catch (error) {
    toggleVisibility(loading, false);
    if (error instanceof Error && error.message === 'FIELD_SELECTION_CANCELLED') {
      return;
    }
    showError(`Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Handle export
function handleExport(): void {
  exportCSV(getGridApi());
}

// Initialize app
function init(): void {
  // Setup filter callbacks
  setFilterCallbacks(applyFilters, renderFilterTree);
  setPresetLoadCallback(applyFilters);

  // Setup upload zone
  setupUploadZone(handleFile);

  // Setup sidebar
  setupSidebar();

  // Setup header buttons
  const filterBtn = getElement('filterBtn');
  const exportBtn = getElement<HTMLButtonElement>('exportBtn');
  const clearFiltersBtn = getElement('clearFiltersBtn');

  if (filterBtn) {
    filterBtn.addEventListener('click', toggleSidebar);
  }

  if (exportBtn) {
    exportBtn.addEventListener('click', handleExport);
  }

  if (clearFiltersBtn) {
    clearFiltersBtn.addEventListener('click', clearAllFilters);
  }

  // Check for migration on load
  checkMigration();
}

// Start app when DOM is ready
document.addEventListener('DOMContentLoaded', init);
